import {
  addDoc,
  collection,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { firestore } from 'services/firebase/client';
import type { GrowlerCustomer } from 'utils/types/GrowlerCustomer';

const COLLECTION = 'growlerCustomers';

const normalizeCpf = (cpf: string): string => cpf.replace(/\D/g, '');

type TimestampLike = { toMillis?: () => number; seconds?: number };

function createdAtMillis(data: Record<string, unknown>): number {
  const value = data.createdAt as TimestampLike | undefined;
  if (value && typeof value.toMillis === 'function') {
    return value.toMillis();
  }
  if (value && typeof value.seconds === 'number') {
    return value.seconds * 1000;
  }
  return Number.MAX_SAFE_INTEGER;
}

/**
 * Upsert por CPF: reutiliza o cadastro mais antigo com o mesmo CPF
 * (preserva id/createdAt) e atualiza os dados da compra atual.
 */
export async function saveGrowlerCustomer(
  customer: Omit<GrowlerCustomer, 'id' | 'createdAt'>,
): Promise<string> {
  const cpf = normalizeCpf(customer.cpf || '');
  if (!cpf) {
    throw new Error('CPF é obrigatório para salvar o cliente growler.');
  }

  const payload = {
    fullName: customer.fullName,
    whatsapp: customer.whatsapp,
    email: customer.email,
    cpf,
    birthDate: customer.birthDate,
    condominium: customer.condominium,
    houseNumber: customer.houseNumber,
    address: customer.address || null,
    uid: customer.uid || null,
    updatedAt: serverTimestamp(),
  };

  const existingQuery = query(
    collection(firestore, COLLECTION),
    where('cpf', '==', cpf),
    limit(10),
  );
  const existingSnap = await getDocs(existingQuery);

  if (!existingSnap.empty) {
    const oldest = [...existingSnap.docs].sort(
      (a, b) => createdAtMillis(a.data()) - createdAtMillis(b.data()),
    )[0];
    await updateDoc(oldest.ref, payload);
    return oldest.id;
  }

  const docRef = await addDoc(collection(firestore, COLLECTION), {
    ...payload,
    createdAt: serverTimestamp(),
  });

  return docRef.id;
}
